import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({
  user: null,
  userRole: null,
  userProfile: null,
  loading: true,
  signIn: async () => { },
  signOut: async () => { },
  updateProfile: async () => { },
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Track in-flight profile fetch promises to prevent duplicates
  const profileFetchPromises = useRef({});
  // Track current user for session checks without causing re-renders
  const currentUserRef = useRef(user);

  // Keep ref in sync with user state
  useEffect(() => {
    currentUserRef.current = user;
  }, [user]);

  // Initialize auth on mount using our custom localStorage-based auth
  useEffect(() => {
    let isMounted = true;
    let timeout;

    const initializeAuth = async () => {
      try {
        console.log('🔐 Starting auth initialization...');

        // Use our custom auth (neonAuthClient) — reads from localStorage
        const { data: { session } } = await supabase.auth.getSession();

        if (!isMounted) return;

        if (session?.user) {
          console.log('✅ Session found for user:', session.user.id);
          const userId = session.user.id;
          setUser(session.user);

          try {
            await Promise.race([
              fetchUserProfile(userId),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Profile fetch timeout')), 20000)
              )
            ]);
            console.log('✅ Profile fetch completed successfully');
          } catch (error) {
            console.error('⚠️ Profile fetch failed during init:', error?.message);
          }
        } else {
          console.log('ℹ️ No active session found');
        }

        if (isMounted) {
          console.log('✅ Auth initialization complete - setting loading to false');
          setLoading(false);
          clearTimeout(timeout);
        }
      } catch (error) {
        if (error?.name === 'AbortError' || error?.message?.includes('AbortError')) {
          console.log('ℹ️ Auth initialization aborted (navigation occurred)');
          return;
        }
        console.error('❌ Auth initialization error:', error);
        if (isMounted) {
          setLoading(false);
          clearTimeout(timeout);
        }
      }
    };

    initializeAuth();

    // Safety timeout
    timeout = setTimeout(() => {
      if (isMounted) {
        console.warn('⏱️ Auth init timeout (30s) - forcing loading to false');
        setLoading(false);
      }
    }, 30000);

    // Listen for auth state changes (our custom auth fires SIGNED_IN/SIGNED_OUT)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;
      console.log('🔐 Auth state changed:', event, 'User ID:', session?.user?.id);

      if (event === 'SIGNED_IN' && session?.user) {
        console.log('✅ Sign in event received');
        setUser(session.user);
        try { await fetchUserProfile(session.user.id); } catch (e) { console.error('Profile fetch error on sign in:', e); }
      } else if (event === 'SIGNED_OUT') {
        console.log('👋 User signed out');
        setUser(null);
        setUserRole(null);
        setUserProfile(null);
      } else if (session?.user) {
        setUser(session.user);
        try { await fetchUserProfile(session.user.id); } catch (e) { console.error('Profile fetch error:', e); }
      } else {
        setUser(null); setUserRole(null); setUserProfile(null);
      }
    });

    return () => {
      isMounted = false;
      clearTimeout(timeout);
      subscription?.unsubscribe();
    };
  }, []);

  // Periodic session check to ensure session is still valid
  useEffect(() => {
    const checkSession = async () => {
      // Only check if user is logged in (use ref to avoid dependency)
      if (!currentUserRef.current) return;

      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('❌ Session check error:', error);
          return;
        }

        if (!session) {
          console.warn('⚠️ Session expired - user will be logged out');
          // Only update state if we currently have a user (prevent redundant updates)
          if (currentUserRef.current) {
            setUser(null);
            setUserRole(null);
            setUserProfile(null);
          }
        } else {
          console.log('✅ Session is valid, expires at:', new Date(session.expires_at * 1000).toLocaleString());
        }
      } catch (error) {
        console.error('❌ Error checking session:', error);
      }
    };

    // Check session after a short delay to allow initial auth to complete
    const initialTimeout = setTimeout(checkSession, 2000);

    // Then check every 5 minutes
    const interval = setInterval(checkSession, 5 * 60 * 1000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []); // Empty dependency array - runs once on mount

  const fetchUserProfile = async (userId, retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000; // Start with 1 second

    // If already fetching this user, return existing promise
    if (profileFetchPromises.current[userId]) {
      console.log('Profile fetch already in flight for', userId);
      return profileFetchPromises.current[userId];
    }

    const promise = (async () => {
      try {
        console.log(`Fetching user profile for userId: ${userId} (attempt ${retryCount + 1}/${MAX_RETRIES})`);

        const { data, error } = await supabase
          ?.from('lasea_users')
          ?.select('*')
          ?.eq('id', userId)
          ?.single();

        if (error) {
          if (error?.name === 'AbortError' || error?.message?.includes('AbortError')) {
            console.log('ℹ️ Profile fetch aborted (navigation occurred)');
            delete profileFetchPromises.current[userId];
            return;
          }
          console.error('User profile fetch error details:', { message: error?.message, userId });
          throw error;
        }

        console.log('✅ User profile fetched successfully:', data);
        if (data) {
          // Merge with any data already in localStorage (e.g. full_name, role from login)
          setUserProfile(data);
          setUserRole(data?.role);
          console.log('✅ Set userRole to:', data?.role);
        } else {
          console.warn('⚠️ User profile not found for ID:', userId);
        }
      } catch (error) {
        // AbortError is expected when navigating away - don't log as error
        if (error?.name === 'AbortError' || error?.message?.includes('AbortError')) {
          console.log('ℹ️ Profile fetch aborted (navigation occurred)');
          return; // Don't re-throw
        }

        console.error('❌ Error fetching user profile after retries:', error);

        // Provide user-friendly error message
        if (error?.message?.includes('Load failed')) {
          console.error('Cannot connect to database. Please check:');
          console.error('1. Your internet connection');
          console.error('2. Supabase project status (may be paused)');
          console.error('3. Environment variables are correct');
        }

        // Re-throw to let caller handle it
        throw error;
      } finally {
        // Clean up the promise from cache
        delete profileFetchPromises.current[userId];
      }
    })();

    // Store the promise to deduplicate concurrent requests
    profileFetchPromises.current[userId] = promise;
    return promise;
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase?.auth?.signInWithPassword({
      email,
      password
    });

    if (error) throw error;

    // Immediately update user state so route guards unblock
    if (data?.user) {
      setUser(data.user);
      setUserRole(data.user.role);
      setUserProfile(data.user);
    }

    return data;
  };

  const signOut = async () => {
    const { error } = await supabase?.auth?.signOut();
    if (error) throw error;

    // Clear local state
    setUser(null);
    setUserRole(null);
    setUserProfile(null);
  };

  const updateProfile = async (updates) => {
    if (!user) throw new Error('No user logged in');
    // Profile updates not yet implemented in the new auth system
    console.warn('updateProfile not yet implemented');
  };

  const value = {
    user,
    userRole,
    userProfile,
    loading,
    signIn,
    signOut,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};